import { MissionResponseData, SiteType } from '../../openapi'

/**
 * プレビュー（おためしプレイ）の初期表示形態。
 * - full: 通常サイズで表示する
 * - hidden: プレビュー自体を表示しない（ホストもマウントしない）
 */
export type PreviewDisplayMode = 'full' | 'hidden'

/**
 * ステージコードの別名を正規化する（rb1 系: 'rjf'/'rj1' → 'rb1'、rb2 系:
 * 'rjs'/'rj2' → 'rb2'）。サイトごとに別名がある（online は 'rb1'/'rb2'、license は
 * 'rj1'/'rj2' 等）ため、いずれの別名でもコースミッションを確実に判定できるよう
 * 大小無視で正規化する。
 *
 * license の体験ステージ 'rj1_trial' はここでは正規化しない。別ステージであって
 * 'rj1' の別名ではなく、ここに混ぜると同梱サムネイル解決（{@link getProjectThumbnail}
 * の別名フォールバック）で rj1_trial_2 が通常版 rj1_2 のサムネイルを引いてしまう。
 * プレビュー可否だけは {@link getPreviewDisplayMode} が個別に許可する。
 */
export const normalizeStageSlug = (slug: string | null | undefined): string => {
  const normalized = slug?.trim().toLowerCase() ?? ''
  if (normalized === 'rjf' || normalized === 'rj1') {
    return 'rb1'
  }
  if (normalized === 'rjs' || normalized === 'rj2') {
    return 'rb2'
  }
  return normalized
}

/**
 * API の slug には rb1/rb2 の別名（rjf/rj1、rjs/rj2）が存在するため、
 * サイトのキー空間（online は rb1・rb2、license は rj1・rj2）に
 * 合わせた slug へ寄せる。
 */
export const toSiteAliasSlug = (
  slug: string | null | undefined,
  siteName: string,
): string => {
  const canonical = normalizeStageSlug(slug)
  if (siteName === SiteType.License) {
    if (canonical === 'rb1') return 'rj1'
    if (canonical === 'rb2') return 'rj2'
  }
  return canonical
}

/**
 * license の体験ステージ。Stg1 の教材（RJF01）を配信しているのは num 1 だけで
 * （src/missions/index.ts の license に同梱されているのも rj1_trial_1 のみ、
 * SCENE_MANIFEST の写像も rj1_trial_1 → RJF01 のみ）、他の番号は OP 動画など
 * プレビュー対象のミッションではない。共有の normalizeStageSlug には入れず、
 * プレビュー可否の判定でだけ Stg1 として許可する。
 */
const LICENSE_TRIAL_SLUG = 'rj1_trial'
const LICENSE_TRIAL_PREVIEWABLE_NUM = 1

/**
 * ミッションごとのプレビュー初期表示形態の純粋な判定。
 *
 * 閲覧中ユーザーのサイトは見ない（それは getPreviewVisible の責務）。ミッションの
 * ステージ（slug）だけで判定する。
 *
 * - Stg1（rb1 系。license の rj1 を含む）: 通常表示
 * - license の体験ステージ rj1_trial: 教材が RJF01 と同じ num 1 のみ通常表示
 * - Stg2（rb2 系。license の rj2 を含む）: 通常表示
 * - ミッション情報の取得前（undefined）: 判定できないため非表示
 *   （取得後にあらためてマウントする）
 * - 上記以外（自由制作・イベント等、コースミッションに紐づかないもの）: 非表示
 */
export const getPreviewDisplayMode = (
  missionInfo: MissionResponseData | undefined,
): PreviewDisplayMode => {
  if (!missionInfo) {
    return 'hidden'
  }
  const rawSlug = missionInfo.slug?.trim().toLowerCase() ?? ''
  if (rawSlug === LICENSE_TRIAL_SLUG) {
    return missionInfo.num === LICENSE_TRIAL_PREVIEWABLE_NUM ? 'full' : 'hidden'
  }
  const slug = normalizeStageSlug(missionInfo.slug)
  if (slug === 'rb1' || slug === 'rb2') {
    return 'full'
  }
  return 'hidden'
}
